import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { usePrintMode } from './PrintModeContext';
import * as Sentry from '@sentry/react';

/**
 * Microsoft Purview Compliance Summary Component
 * Displays data governance, DLP alerts, sensitivity labels, and retention policy metrics
 */
const MsPurviewSummary = () => {
  const { apiFetch } = useAuth();
  const isPrintMode = usePrintMode();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line

  async function fetchData() {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch('/api/mspurview/summary');
      if (!response.ok) throw new Error('Failed to fetch MS Purview summary');
      const json = await response.json();
      setData(json);
    } catch (e) {
        Sentry.captureException(e);
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  // Calculate health score and interpretation
  const healthScore = useMemo(() => {
    if (!data) return null;
    const score = data.healthScore ?? 0;
    let interpretation, color, icon;
    if (score >= 95) {
      interpretation = 'Excellent';
      color = '#2e7d32';
      icon = '✓';
    } else if (score >= 90) {
      interpretation = 'Good';
      color = '#7cb342';
      icon = '✓';
    } else if (score >= 80) {
      interpretation = 'Fair';
      color = '#ffa000';
      icon = '⚠';
    } else if (score >= 70) {
      interpretation = 'Needs Attention';
      color = '#ff5722';
      icon = '⚠';
    } else {
      interpretation = 'Critical';
      color = '#d32f2f';
      icon = '✗';
    }
    return { score, interpretation, color, icon };
  }, [data]);

  // Prepare chart data
  const dlpAlertsBySeverity = useMemo(() => {
    if (!data) return [];
    return [
      { name: 'Critical', value: data.criticalDlpAlerts || 0, color: '#d32f2f' },
      { name: 'High', value: data.highDlpAlerts || 0, color: '#ff5722' },
      { name: 'Medium', value: data.mediumDlpAlerts || 0, color: '#ffa000' },
      { name: 'Low', value: data.lowDlpAlerts || 0, color: '#7cb342' }
    ].filter(item => item.value > 0);
  }, [data]);

  const dlpAlertsStatus = useMemo(() => {
    if (!data) return [];
    return [
      { name: 'Open', value: data.openDlpAlerts || 0, color: '#ff5722' },
      { name: 'Resolved', value: data.resolvedDlpAlerts || 0, color: '#4CAF50' }
    ].filter(item => item.value > 0);
  }, [data]);

  // Microsoft Secure Score category breakdown (Identity, Data, Device, Apps, Infrastructure).
  // Added as a well-rounded, board-level security posture metric because retention label data
  // cannot be retrieved under this app's authentication model (see MsPurviewService.cs
  // GetRetentionLabelsAsync doc comment). Secure Score IS available via Application permissions.
  const secureScoreCategories = useMemo(() => {
    if (!data?.secureScoreCategoryBreakdown?.length) return [];
    const palette = ['#f63040', '#2196F3', '#7cb342', '#ffa000', '#7e57c2', '#26a69a'];
    // value is now a normalized percentage (achievedPoints / maxPoints * 100), not a raw point
    // sum - raw points aren't comparable across categories since categories don't all have the
    // same total possible points (see MsPurviewService.cs GetSecureScoreControlProfilesAsync).
    return data.secureScoreCategoryBreakdown.map((c, i) => ({
      name: c.category,
      value: c.percentage ?? 0,
      achievedPoints: c.achievedPoints,
      maxPoints: c.maxPoints,
      color: palette[i % palette.length]
    }));
  }, [data]);

  // Deterministic "what this means / what to fix" narrative. This renders regardless of
  // whether AI insights are enabled, so the report is always self-explanatory for the
  // financial director / incoming IT lead reviewing Purview maturity.
  const narrative = useMemo(() => {
    if (!data) return null;

    const findings = [];
    const actions = [];

    const labelCoverage = data.labelCoverageRate ?? 0;
    const retentionRate = data.retentionPolicyActiveRate ?? 0;
    const totalLabels = data.totalSensitivityLabels ?? 0;
    const totalRetention = data.totalRetentionLabels ?? 0;
    const totalCompliance = data.totalCompliancePolicies ?? 0;
    const openAlerts = data.openDlpAlerts ?? 0;

    // Sensitivity labels
    if (!data.hasSensitivityLabels || totalLabels === 0) {
      findings.push('No sensitivity labels are published, so documents and emails cannot be automatically classified or protected.');
      actions.push('Create and publish a baseline sensitivity-label set (e.g. Public, Internal, Confidential, Highly Confidential).');
    } else if (labelCoverage < 100) {
      findings.push(`Sensitivity labels exist but coverage is ${labelCoverage}% — not all labels are enabled or scoped to users.`);
      actions.push('Enable the remaining sensitivity labels and assign label policies to all user groups.');
    }

    // Encryption
    if ((data.labelsWithEncryption ?? 0) === 0 && totalLabels > 0) {
      actions.push('Apply encryption to at least the Confidential / Highly Confidential labels.');
    }

    // DLP policies. hasActiveDlpPolicies is one-sided: a "No" here only means this integration
    // didn't find a policy scoped to its own app activity - it cannot see policies scoped to
    // Exchange, Teams, SharePoint or Endpoint locations, which is how DLP is normally configured
    // (see MsPurviewService.cs GetCompliancePoliciesAsync doc comment). Don't assert this as a
    // confirmed tenant-wide gap.
    if (!data.hasActiveDlpPolicies) {
      findings.push('This integration did not detect a DLP policy scoped to its own activity - that does not confirm the tenant has no DLP policies, since Exchange/Teams/SharePoint/Endpoint-scoped policies aren\'t visible to this check. Verify current DLP policy status directly in the Purview compliance portal (Data Loss Prevention → Policies).');
      actions.push('Confirm DLP policy coverage and enforcement mode in the Purview compliance portal rather than relying solely on this report for that specific check.');
    } else if (openAlerts > 0) {
      findings.push(`${openAlerts} DLP alert(s) are still open and awaiting review.`);
      actions.push('Triage and resolve the open DLP alerts, then tune noisy rules.');
    }

    // Retention. data.retentionDataAvailable is false whenever Microsoft Graph's retention
    // label API couldn't be reached - it's Delegated-only and can never respond under this
    // app's app-only auth (see MsPurviewService.cs GetRetentionLabelsAsync). That is an
    // integration limitation, not evidence the tenant has no retention policies, so it must be
    // reported distinctly from a confirmed zero.
    if (data.retentionDataAvailable === false) {
      findings.push('Retention policy status could not be verified through this integration — Microsoft Graph\'s retention label API requires delegated (signed-in user) authentication, which this app does not use.');
      actions.push('Verify retention policy configuration directly in the Microsoft Purview compliance portal, or extend this integration with a delegated auth flow.');
    } else if (!data.hasRetentionPolicies || totalRetention === 0) {
      findings.push('No retention policies are configured, leaving the company without a defined data-keeping / deletion schedule (a compliance and legal-hold risk).');
      actions.push('Define retention policies aligned to legal/financial record-keeping requirements.');
    } else if (retentionRate < 100) {
      findings.push(`Retention policies exist but only ${retentionRate}% are active.`);
      actions.push('Activate the remaining retention policies.');
    }

    // Compliance policies
    if (totalCompliance === 0) {
      actions.push('Configure compliance policies (e.g. communication compliance, insider-risk) once the foundations above are in place.');
    }

    // Microsoft Secure Score - well-rounded security posture metric, independent of the
    // Purview-specific gaps above.
    if (data.hasSecureScoreData) {
      const pct = data.secureScorePercentage ?? 0;
      const industryAvg = data.secureScoreIndustryAverage;
      if (pct < 50) {
        findings.push(`Microsoft Secure Score is ${pct}% (${data.secureScoreCurrent}/${data.secureScoreMax}) — the tenant's overall security configuration is weak.`);
        actions.push('Review the lowest-scoring Secure Score categories in the Microsoft Defender portal and action the top recommendations.');
      } else if (industryAvg != null && data.secureScoreMax > 0 && data.secureScoreCurrent < industryAvg) {
        findings.push(`Secure Score (${data.secureScoreCurrent}/${data.secureScoreMax}) is below the industry-peer average (${Math.round(industryAvg)}).`);
        actions.push('Compare against peer organisations in Microsoft Defender to identify quick-win improvements.');
      }
    }

    const score = healthScore?.score ?? 0;
    let context;
    if (score >= 80) {
      context = 'Purview is largely configured; the items below are refinements rather than gaps.';
    } else if (score >= 50) {
      context = 'Purview is partially configured. The foundational controls below should be prioritised to reduce data-governance risk.';
    } else {
      context = 'Purview is still being stood up. A low score here is expected during setup — it is a roadmap of what to configure, not a reflection of day-to-day IT performance.';
    }

    return { context, findings, actions };
  }, [data, healthScore]);

  // LLM proxy text for AI summary
  const llmProxyText = useMemo(() => {
    if (!data) return '';
    return [
      `Compliance Health Score: ${healthScore?.score}/100 (${healthScore?.interpretation})`,
      `Total DLP Alerts: ${data.totalDlpAlerts || 0}`,
      `Open Alerts: ${data.openDlpAlerts || 0}, Resolved: ${data.resolvedDlpAlerts || 0}`,
      `Alert Resolution Rate: ${data.dlpAlertResolutionRate || 0}%`,
      `Critical Alerts: ${data.criticalDlpAlerts}, High: ${data.highDlpAlerts}, Medium: ${data.mediumDlpAlerts}, Low: ${data.lowDlpAlerts}`,
      `Sensitivity Labels: ${data.totalSensitivityLabels || 0} total, ${data.enabledSensitivityLabels} enabled (${data.labelCoverageRate}% coverage)`,
      `Labels with Encryption: ${data.labelsWithEncryption || 0}`,
      data.retentionDataAvailable === false
        ? 'Retention Policies: not verifiable (Graph retention label API requires delegated auth)'
        : `Retention Policies: ${data.totalRetentionLabels || 0} total, ${data.activeRetentionPolicies} active (${data.retentionPolicyActiveRate}% rate)`,
      `Compliance Policies: ${data.totalCompliancePolicies || 0} total, ${data.activeCompliancePolicies} active (${data.policyActiveRate}% rate)`,
      `Active DLP Policies: ${data.hasActiveDlpPolicies ? 'Yes' : 'No'}`,
      `Sensitivity Labels Configured: ${data.hasSensitivityLabels ? 'Yes' : 'No'}`,
      `Retention Policies Configured: ${data.hasRetentionPolicies ? 'Yes' : 'No'}`,
      data.hasSecureScoreData
        ? `Microsoft Secure Score: ${data.secureScoreCurrent}/${data.secureScoreMax} (${data.secureScorePercentage}%)`
        : 'Microsoft Secure Score: not reporting'
    ].join('\n');
  }, [data, healthScore]);

  if (loading) return <HexagonLoader message="Loading Microsoft Purview data..." />;
  if (error) return <div className="dashboard-card error-card"><p>Error: {error}</p></div>;
  if (!data) return <div className="dashboard-card info-card"><p>No Microsoft Purview data available</p></div>;

  return (
    <div className="dashboard-card group-wrapper mspurview-summary">
      <div data-llm-proxy style={{ display: 'none' }}>{llmProxyText}</div>
      
      <h2 style={{ marginTop: 0, marginBottom: 12 }}>Microsoft Purview Compliance</h2>

      {/* Health Score Banner — matches the standard exec-banner-layout used by every other
          top-level report section (Sentry, GitHub, Backup, Device Compliance, etc.) so
          Purview doesn't stand out as a visual outlier. */}
      {healthScore && (
        <div className="dashboard-card exec-banner" style={{ borderLeft: `4px solid ${healthScore.color}` }}>
          <div className="exec-banner-layout">
            <div className="exec-banner-score">
              <div className="exec-banner-value" style={{ color: healthScore.color }}>
                {healthScore.score}
                <span className="exec-banner-suffix">/ 100</span>
              </div>
              <div className="exec-banner-label" style={{ color: healthScore.color }}>{healthScore.icon} {healthScore.interpretation}</div>
              <div className="exec-banner-sublabel">Compliance Health</div>
            </div>
            <div className="exec-banner-body">
              <div className="exec-banner-text">
                <strong>What this means:</strong> {narrative?.context}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deterministic explanation block — always shown so directors and the incoming
          IT lead understand the score even when AI insights are disabled. */}
      {narrative && (narrative.findings.length > 0 || narrative.actions.length > 0) && (
        <div
          className="dashboard-card"
          style={{
            borderLeft: `4px solid ${healthScore?.color || '#777'}`,
            background: '#fafbfc',
            padding: '12px 16px',
            marginBottom: 12
          }}
        >
          {narrative.findings.length > 0 && (
            <div style={{ marginBottom: narrative.actions.length ? 8 : 0 }}>
              <div style={{ fontSize: 12, textTransform: 'uppercase', color: '#666', marginBottom: 4 }}>Current gaps</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                {narrative.findings.map((f, i) => <li key={`f-${i}`}>{f}</li>)}
              </ul>
            </div>
          )}
          {narrative.actions.length > 0 && (
            <div>
              <div style={{ fontSize: 12, textTransform: 'uppercase', color: '#666', marginBottom: 4 }}>Recommended next steps</div>
              <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                {narrative.actions.map((a, i) => <li key={`a-${i}`}>{a}</li>)}
              </ol>
            </div>
          )}
        </div>
      )}

      {/* Key Metrics */}
      <div className="metrics-row">
        <div className="dashboard-card metric-card">
          <div className="metric-label">DLP Alerts</div>
          <div className="metric-value">{data.totalDlpAlerts || 0}</div>
          <div className="metric-subtitle">
            {data.openDlpAlerts} open • {data.resolvedDlpAlerts} resolved
          </div>
        </div>

        <div className="dashboard-card metric-card">
          <div className="metric-label">Alert Resolution Rate</div>
          <div className="metric-value">{data.dlpAlertResolutionRate || 0}%</div>
          <div className="metric-subtitle">
            {data.criticalDlpAlerts} critical • {data.highDlpAlerts} high priority
          </div>
        </div>

        <div className="dashboard-card metric-card">
          <div className="metric-label">Sensitivity Labels</div>
          <div className="metric-value">{data.enabledSensitivityLabels || 0}</div>
          <div className="metric-subtitle">
            {data.labelsWithEncryption} with encryption • {data.labelCoverageRate}% coverage
          </div>
        </div>

        <div className="dashboard-card metric-card">
          <div className="metric-label">Retention Policies</div>
          <div className="metric-value">
            {data.retentionDataAvailable === false ? 'N/A' : (data.activeRetentionPolicies || 0)}
          </div>
          <div className="metric-subtitle">
            {data.retentionDataAvailable === false
              ? 'Not verifiable via this integration'
              : `${data.totalRetentionLabels} total labels • ${data.retentionPolicyActiveRate}% active`}
          </div>
        </div>

        <div className="dashboard-card metric-card">
          <div className="metric-label">Microsoft Secure Score</div>
          <div className="metric-value">
            {data.hasSecureScoreData ? `${data.secureScorePercentage}%` : 'N/A'}
          </div>
          <div className="metric-subtitle">
            {data.hasSecureScoreData
              ? `${data.secureScoreCurrent} / ${data.secureScoreMax} points`
              : 'Not reporting'}
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-row">
        {/* DLP Alerts by Severity */}
        <div className="dashboard-card chart-card">
          <h3>DLP Alerts by Severity</h3>
          <ResponsiveContainer width="100%" height={isPrintMode ? 200 : 250}>
            <BarChart data={dlpAlertsBySeverity}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#2196F3" isAnimationActive={!isPrintMode}>
                {dlpAlertsBySeverity.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* DLP Alert Status */}
        <div className="dashboard-card chart-card">
          <h3>DLP Alert Status</h3>
          <ResponsiveContainer width="100%" height={isPrintMode ? 200 : 250}>
            <PieChart>
              <Pie
                data={dlpAlertsStatus}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={isPrintMode ? 70 : 80}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                isAnimationActive={!isPrintMode}
              >
                {dlpAlertsStatus.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
                    </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Microsoft Secure Score by Category - added as a well-rounded, board-level security
                    posture metric since retention label data cannot be retrieved under this app's
                    authentication model (Delegated-only Graph API vs. app-only auth). */}
                {secureScoreCategories.length > 0 && (
                  <div className="dashboard-card chart-card">
                    <h3>Secure Score by Category (%)</h3>
                    <ResponsiveContainer width="100%" height={isPrintMode ? 200 : 250}>
                      <BarChart data={secureScoreCategories} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 100]} unit="%" />
                        <YAxis type="category" dataKey="name" width={90} />
                        <Tooltip formatter={(value, _name, props) => [
                          `${value}% (${props.payload.achievedPoints ?? 0}/${props.payload.maxPoints ?? 0} pts)`,
                          'Score'
                        ]} />
                        <Bar dataKey="value" fill="#f63040" isAnimationActive={!isPrintMode}>
                          {secureScoreCategories.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

      {/* Policy Coverage Section */}
      <div className="metrics-row">
        <div className="dashboard-card info-section">
          <h3>Policy Coverage</h3>
          <div className="status-grid">
            <div className="status-item">
              <span className="status-label">Sensitivity Label Coverage:</span>
              <span className="status-value">{data.labelCoverageRate || 0}%</span>
              <span className="status-detail">({data.enabledSensitivityLabels} / {data.totalSensitivityLabels})</span>
            </div>
            <div className="status-item">
              <span className="status-label">Retention Policy Active Rate:</span>
              <span className="status-value">
                {data.retentionDataAvailable === false ? 'N/A' : `${data.retentionPolicyActiveRate || 0}%`}
              </span>
              <span className="status-detail">
                {data.retentionDataAvailable === false
                  ? 'not verifiable via this integration'
                  : `(${data.activeRetentionPolicies} / ${data.totalRetentionLabels})`}
              </span>
            </div>
            <div className="status-item">
              <span className="status-label">Compliance Policy Active Rate:</span>
              <span className="status-value">{data.policyActiveRate || 0}%</span>
              <span className="status-detail">({data.activeCompliancePolicies} / {data.totalCompliancePolicies})</span>
            </div>
            <div className="status-item">
              <span className="status-label">Labels with Encryption:</span>
              <span className="status-value">{data.labelsWithEncryption || 0}</span>
            </div>
          </div>
        </div>

        <div className="dashboard-card info-section">
          <h3>Compliance Posture</h3>
          <div className="status-grid">
            <div className="status-item">
              <span className="status-label">Active DLP Policies:</span>
              <span className={`status-value ${data.hasActiveDlpPolicies ? '' : 'warn-text'}`}>
                {data.hasActiveDlpPolicies ? 'Yes' : 'Not detected'}
              </span>
              {!data.hasActiveDlpPolicies && (
                <span className="status-detail">verify in Purview portal — see caveat below</span>
              )}
            </div>
            <div className="status-item">
              <span className="status-label">Sensitivity Labels Configured:</span>
              <span className={`status-value ${data.hasSensitivityLabels ? '' : 'warn-text'}`}>
                {data.hasSensitivityLabels ? 'Yes' : 'No'}
              </span>
            </div>
            <div className="status-item">
              <span className="status-label">Retention Policies Configured:</span>
              <span className={`status-value ${data.hasRetentionPolicies === false ? 'warn-text' : ''}`}>
                {data.hasRetentionPolicies === null || data.hasRetentionPolicies === undefined
                  ? 'Unverifiable'
                  : (data.hasRetentionPolicies ? 'Yes' : 'No')}
              </span>
            </div>
            <div className="status-item">
              <span className="status-label">Total Compliance Policies:</span>
              <span className="status-value">{data.totalCompliancePolicies || 0}</span>
            </div>
            <div className="status-item">
              <span className="status-label">Microsoft Secure Score:</span>
              <span className={`status-value ${data.hasSecureScoreData && data.secureScorePercentage < 50 ? 'warn-text' : ''}`}>
                {data.hasSecureScoreData ? `${data.secureScorePercentage}%` : 'Not reporting'}
              </span>
              {data.hasSecureScoreData && (
                <span className="status-detail">({data.secureScoreCurrent} / {data.secureScoreMax} points)</span>
              )}
            </div>
          </div>
          {!data.hasActiveDlpPolicies && data.compliancePolicyScopeCaveat && (
            <div style={{ fontSize: 12, color: '#666', marginTop: 10, paddingTop: 10, borderTop: '1px solid #eee' }}>
              <strong>Note:</strong> {data.compliancePolicyScopeCaveat}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MsPurviewSummary;
